import Axios from "helpers/Interceptor";
import { CurrentBrand } from "modules/Types";

interface FacebookAdAccount {
    id: string;
    name: string;
    currency: string;
    account_status: string;
    amount_spent: string;
    business?: {
        id: string;
        name: string;
    };
}

interface FacebookInfo {
    fbAdAccount: FacebookAdAccount;
    loggedInFbUser: {
        access_token: string;
    };
}

export const getFacebookInformation_api = async (brandId: number): Promise<FacebookInfo | null> => {
    try {
        const response = await Axios.get<FacebookInfo>(`/admin/ad-account/${brandId}`);
        return response.data;
    } catch (error) {
        return null;
    }
}

export const getCampaingInformation_api = async (campaignId: number): Promise<any | null> => {
    try {
        const response = await Axios.get<any>(`/admin/campaign-info/${campaignId}`);
        return response.data;
    } catch (error) {
        return null;
    }
}

/** Interface for partner data */
export interface PartnerDiscountType {
    discountAmount?: number;
    partnerId: string;
    partnerName: string;
    cookieExpirationFromSignup?: number;
    startDate: string;
    appliesTo?: string;
}

/**
 * Fetches all affiliate partner keys from the admin API
 * @returns Promise resolving to an array of partner strings or null if the request fails
 */
export const getAffiliatePartners_api = async (): Promise<PartnerDiscountType[] | null> => {
    try {
        const response = await Axios.get<PartnerDiscountType[]>('/admin/get-affiliate-partners');
        return response.data;
    } catch (error) {
        return null;
    }
}

/** Response type for the affiliate partner snapshot API */
export interface AffiliatePartnerSnapshotResponse {
    users: {
        free: number;
        paid: number;
        overall: number;
        trialing: number;
        pro?: {
            annual?: {
                active: number;
                unpaid: number;
                canceled: number;
                past_due: number;
                trialing: number;
            };
            monthly?: {
                active: number;
                unpaid: number;
                canceled: number;
                past_due: number;
                trialing: number;
            };
        };
        lite?: {
            annual?: {
                active: number;
                unpaid: number;
                canceled: number;
                past_due: number;
                trialing: number;
            };
            monthly?: {
                active: number;
                unpaid: number;
                canceled: number;
                past_due: number;
                trialing: number;
            };
        };
    };
    payments: {
        successful: number;
        gross_revenue: number;
        net_revenue?: number;
        pro?: {
            annual?: {
                net: number;
                count: number;
                gross: number;
            };
            monthly?: {
                net: number;
                count: number;
                gross: number;
            };
        };
        lite?: {
            annual?: {
                net: number;
                count: number;
                gross: number;
            };
            monthly?: {
                net: number;
                count: number;
                gross: number;
            };
        };
    };
    campaigns: {
        totalSpend: number;
        draftCampaigns: number;
        totalCampaigns: number;
        nonDraftCampaigns: number;
    };
    totalSignups: number;
    trialConversion: {
        started: number;
        canceled: number;
        trialing: number;
        converted: number;
        payment_failure_cancellation: number;
        pro?: {
            annual?: {
                started: number;
                canceled: number;
                converted: number;
                payment_failure: number;
            };
            monthly?: {
                started: number;
                canceled: number;
                converted: number;
                payment_failure: number;
            };
        };
        lite?: {
            annual?: {
                started: number;
                canceled: number;
                converted: number;
                payment_failure: number;
            };
            monthly?: {
                started: number;
                canceled: number;
                converted: number;
                payment_failure: number;
            };
        };
    };
}

/**
 * Fetches snapshot data for a specific affiliate partner
 * @param partnerId - The unique identifier for the partner
 * @returns Promise resolving to the snapshot data or null if the request fails
 */
export const getAffiliatePartnerSnapshot_api = async (partnerId: string): Promise<AffiliatePartnerSnapshotResponse | null> => {
    try {
        const response = await Axios.post<{snapshot: AffiliatePartnerSnapshotResponse}>('/admin/get-affiliate-partner-snapshot', { partnerId });
        return response.data.snapshot;
    } catch (error) {
        return null;
    }
}

/**
 * Updates a brand's subscription plan and trial settings via admin API
 * 
 * @param params - Configuration for the subscription update
 * @param params.planType - The plan type to set for the brand
 * @param params.trialDays - Optional number of trial days if creating a trial
 * @param params.userEmail - The email of the user to update the subscription for
 * @param params.brandId - The unique identifier for the brand
 * @returns Promise resolving to the API response data
 */
export const createTrialForBrand = async (
    {
        planType,
        trialDays = 14,
        userEmail,
        brandId
    }: {
        planType: string;
        trialDays?: number;
        userEmail: string;
        brandId: number,
    }
): Promise<any> => {
    const response = await Axios.post<CurrentBrand>(`/admin/create-trial-subscription`, {
        planType,
        trialDays,
        userEmail,
        brandId
    });
    return response.data;
};

/**
 * Fetches top performing brands data for admin dashboard
 * 
 * @param params - Query parameters for filtering and pagination
 * @param params.limit - Maximum number of results to return
 * @param params.offset - Number of results to skip (for pagination)
 * @param params.timeRange - Time period to analyze ('7d', '14d', '30d', 'custom')
 * @param params.startDate - Optional start date (only required for custom time range)
 * @param params.endDate - Optional end date (only required for custom time range)
 * @param params.type - Optional event type to filter by ('Page View', 'StreamingLinkClicked', etc.)
 * @returns Promise resolving to the top performing brands data
 */
export const getTopPerformingBrands_api = async (
  params: {
    limit: number;
    offset: number;
    timeRange: string;
    startDate?: string;
    endDate?: string;
    type?: string;
  }
): Promise<any> => {
  try {
    const response = await Axios.get('/admin/top-performing', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching top performing brands:', error);
    throw error;
  }
};

/**
 * Processes a video to generate clips at specified intervals
 * 
 * @param params - Processing parameters
 * @param params.videoUrl - The URL of the video to process
 * @param params.videoId - The Cloudinary public_id of the video
 * @param params.clipInterval - Time interval in seconds for each clip
 * @param params.clipLength - Optional length/duration in seconds for each clip
 * @returns Promise resolving to the generated clips data
 */
export const processVideo_api = async (
  params: {
    videoUrl: string;
    videoId: string;
    clipInterval: number;
    clipLength?: number;
  }
): Promise<{
  success: boolean;
  clips: Array<{
    id: string;
    url: string;
    thumbnailUrl: string;
    startTime: number;
    endTime: number;
    duration: number;
  }>;
}> => {
  try {
    const response = await Axios.post('/admin/process-video', params);
    return response.data;
  } catch (error) {
    console.error('Error processing video:', error);
    throw error;
  }
};
